import { Link } from "@tanstack/react-router";
import { CheckCircle2, ArrowRight, Phone, MapPin } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";

import { PricingTeaserStrip } from "@/components/PricingTeaserStrip";
import type { CityKey, CityPage } from "@/lib/citiesData";
import { CITY_PAGES } from "@/lib/citiesData";
import { getCityInvestment } from "@/lib/investmentRanges";
import { SCHEMA_PERSON_ID, SCHEMA_ORG_ID, buildSpeakableSchema } from "@/lib/entitySchema";

const SITE_URL = "https://coloradowebimpressions.com";


export type CostService = "seo" | "web-design" | "google-maps";

type ServiceMeta = {
  label: string;
  productLabel: string;
  bookCtaLabel: string;
  serviceHubPath: string;
  defaultLowHighDescriptor: string;
};

const SERVICE_META: Record<CostService, ServiceMeta> = {
  seo: {
    label: "SEO",
    productLabel: "monthly SEO program",
    bookCtaLabel: "See SEO services",
    serviceHubPath: "/colorado-springs-seo",
    defaultLowHighDescriptor: "Most engagements run month-to-month with no long-term contract.",
  },
  "web-design": {
    label: "Web Design",
    productLabel: "one-time website build",
    bookCtaLabel: "See web design services",
    serviceHubPath: "/colorado-springs-web-design",
    defaultLowHighDescriptor: "One-time website pricing includes the first year of hosting and a built-in traffic dashboard.",
  },
  "google-maps": {
    label: "Google Maps",
    productLabel: "monthly Google Business Profile + Map Pack program",
    bookCtaLabel: "See Google Maps services",
    serviceHubPath: "/colorado-springs-google-maps",
    defaultLowHighDescriptor: "Most engagements run month-to-month with no long-term contract.",
  },
};

function getCity(slug: CityKey): CityPage {
  const c = CITY_PAGES.find((c) => c.slug === slug);
  if (!c) throw new Error(`Unknown city: ${slug}`);
  return c;
}

/**
 * Build the head() for a /[city]-[service]-cost route. Returns a fully-formed
 * TanStack head config including title, description, OG, canonical, and
 * Article + FAQPage + Speakable JSON-LD.
 */
export function buildCityServiceCostHead(city: CityKey, service: CostService) {
  const c = getCity(city);
  const meta = SERVICE_META[service];
  const path = `/${city}-${service}-cost`;
  const inv = service === "google-maps"
    ? getCityInvestment(city, "seo", c.name) // google maps uses SEO range
    : getCityInvestment(city, service, c.name);

  const title = `${c.name} ${meta.label} Cost ${new Date().getFullYear()} | What ${c.name} Businesses Actually Pay`;
  const description = `What does ${meta.label.toLowerCase()} cost in ${c.name}, CO? Real pricing ranges, what's included at each tier, and the local-market factors that drive ${meta.label.toLowerCase()} cost in ${c.name}. Updated for ${new Date().getFullYear()}.`;

  const today = new Date().toISOString().split("T")[0];

  const article = {
    "@context": "https://schema.org",
    "@type": "Article",
    mainEntityOfPage: `${SITE_URL}${path}`,
    headline: title,
    description,
    datePublished: "2026-04-30",
    dateModified: today,
    image: `${SITE_URL}/logo.png`,
    author: { "@id": SCHEMA_PERSON_ID },
    publisher: { "@id": SCHEMA_ORG_ID },
    about: [`${c.name} ${meta.label} cost`, `${c.name} ${meta.label} pricing`, "Colorado SEO pricing"],
  };

  const faqs = [
    {
      q: `How much does ${meta.label.toLowerCase()} cost in ${c.name}?`,
      a: `In ${c.name}, our ${meta.productLabel} starts at ${service === "web-design" ? "$1,850 one-time" : "$950/month"}. ${inv.note}`,
    },
    {
      q: `Why does ${meta.label.toLowerCase()} cost more or less in ${c.name} vs. other Colorado cities?`,
      a: `Our published package prices are the same across Colorado. Page count, services, and optional add-ons determine your quote; compare full packages on /pricing.`,
    },
    {
      q: `What's actually included at this price?`,
      a: inv.bullets.join(" · "),
    },
    {
      q: `Are there long-term contracts?`,
      a: `No. All ongoing work is month-to-month. Cancel anytime.`,
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  // BreadcrumbList — required by SEO audit so search engines understand
  // this page sits two levels deep (Home → {City} → {Service} Cost).
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: c.name, item: `${SITE_URL}/${city}` },
      { "@type": "ListItem", position: 3, name: `${meta.label} Cost in ${c.name}` },
    ],
  };

  // Service schema — the page describes a priced service offering. Emitting
  // this lets Google attach Offer / PriceSpecification rich-result eligibility
  // and connects the page to the LocalBusiness entity via provider @id.
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_URL}${path}#service`,
    name: `${meta.label} for ${c.name} businesses`,
    description,
    serviceType: meta.label,
    provider: { "@id": SCHEMA_ORG_ID },
    areaServed: { "@type": "City", name: c.name, containedInPlace: { "@type": "State", name: "Colorado" } },
    offers: {
      "@type": "Offer",
      priceCurrency: "USD",
      priceSpecification: { "@type": "PriceSpecification", priceCurrency: "USD", price: service === "web-design" ? 1850 : 950 },
      availability: "https://schema.org/InStock",
      seller: { "@id": SCHEMA_ORG_ID },
    },
  };

  return {
    meta: [
      { title },
      { name: "description", content: description },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "article:modified_time", content: today },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${path}` }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(article) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
      { type: "application/ld+json", children: JSON.stringify(serviceSchema) },
      { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`${SITE_URL}${path}`, title)) },
    ],
  };
}

export function CityServiceCostPage({ city, service }: { city: CityKey; service: CostService }) {
  const c = getCity(city);
  const meta = SERVICE_META[service];
  const inv = service === "google-maps"
    ? getCityInvestment(city, "seo", c.name)
    : getCityInvestment(city, service, c.name);
  const cityServiceSpoke: string = `/${city}-${service === "google-maps" ? "seo" : service}`;

  return (
    <SiteLayout>
      <PageHero
        eyebrow={`${c.name} · ${meta.label} Cost`}
        title={
          <>
            What does {meta.label.toLowerCase()} cost in <span className="text-gradient-gold">{c.name}</span>?
          </>
        }
        description={`Real ${meta.label.toLowerCase()} pricing for ${c.name} businesses — no "request a quote" games. Below: the typical range, what's included at each tier, and the local factors that move it.`}
      />

      <PricingTeaserStrip />



      <section className="bg-section-alt py-12 lg:py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="text-2xl font-extrabold text-foreground">
            What's actually included at this price in {c.name}
          </h2>
          <ul className="mt-6 space-y-3">
            {inv.bullets.map((b) => (
              <li key={b} className="flex items-start gap-3 rounded-lg border border-border bg-background p-4 text-sm text-foreground">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold-deep" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-background py-12 lg:py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 space-y-8">
          <div>
            <h2 className="text-2xl font-extrabold text-foreground">
              Why {meta.label.toLowerCase()} costs what it does in {c.name}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Pricing in {c.name} reflects local competition density, typical
              project scope, and buyer expectations. We don't charge more in
              premium markets for the same work — we charge more because the
              scope is genuinely larger (more service-area pages, deeper
              keyword surface, faster review cadence).
            </p>
          </div>

          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
              <MapPin className="h-4 w-4 text-brand-gold-deep" />
              Local context for {c.name}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {c.intro}
            </p>
          </div>
        </div>
      </section>

      <section className="bg-section-alt py-12 lg:py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="text-2xl font-extrabold text-foreground">
            Common questions about {meta.label.toLowerCase()} cost in {c.name}
          </h2>
          <div className="mt-6 space-y-3">
            {[
              {
                q: `How much does ${meta.label.toLowerCase()} cost in ${c.name}?`,
                a: `${inv.priceRange} for our ${meta.productLabel}. ${inv.note}`,
              },
              {
                q: `Are there long-term contracts?`,
                a: `No. Month-to-month. Cancel anytime.`,
              },
              {
                q: `What if I'm a smaller ${c.name} business — can you do less?`,
                a: `Sometimes. We'd rather be honest about fit than sell you a scope that won't move the needle. Book a call and we'll tell you straight.`,
              },
              {
                q: `Do you do project-based ${meta.label.toLowerCase()} in ${c.name}?`,
                a: meta.label === "Web Design"
                  ? `Yes — websites are project-based. The range above reflects typical ${c.name} scope.`
                  : `Rarely. ${meta.label} is an ongoing program — one-and-done sprints don't sustain rankings.`,
              },
            ].map((f) => (
              <details key={f.q} className="group rounded-lg border border-border bg-background p-4">
                <summary className="cursor-pointer text-sm font-semibold text-foreground marker:hidden">
                  {f.q}
                </summary>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>


      <section className="bg-background py-12 lg:py-16">

        <div className="mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <h2 className="text-2xl font-extrabold text-foreground">
            Ready to see a real {c.name} {meta.label.toLowerCase()} plan?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">
            We'll audit your current footprint and quote a {c.name}-specific scope on the call. No pressure.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/free-audit" className="inline-flex items-center gap-2 rounded-lg bg-brand-gold-deep px-5 py-3 text-sm font-semibold text-background transition-smooth hover:bg-brand-gold">
              Get my free audit
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="tel:+17194193935" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-smooth hover:border-brand-gold/60" aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" />
              (719) 419-3935
            </a>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-xs">
            <a href={cityServiceSpoke} className="text-muted-foreground underline-offset-4 hover:text-brand-gold-deep hover:underline">
              {c.name} {meta.label} services →
            </a>
            <a href={meta.serviceHubPath} className="text-muted-foreground underline-offset-4 hover:text-brand-gold-deep hover:underline">
              {meta.bookCtaLabel} →
            </a>
            <Link to="/pricing" className="text-muted-foreground underline-offset-4 hover:text-brand-gold-deep hover:underline">
              All pricing →
            </Link>
            <Link to="/learn/cost-guides" className="text-muted-foreground underline-offset-4 hover:text-brand-gold-deep hover:underline">
              All Colorado cost guides →
            </Link>
            <a
              href={
                service === "web-design"
                  ? "/learn/web-design-cost-colorado"
                  : service === "seo"
                    ? "/learn/monthly-lead-generation-cost-colorado"
                    : "/learn/google-maps-optimization-cost-colorado"
              }
              className="text-muted-foreground underline-offset-4 hover:text-brand-gold-deep hover:underline"
            >
              Statewide {meta.label.toLowerCase()} cost guide →
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
